import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, TrendingUp, TrendingDown } from 'lucide-react';
import useStore from '@/store/useStore';
import { apiGetTransactions } from '@/services/api';
import { getSelectedMerchantId, storeRoute } from '@/lib/routes';

export default function HistoryPage() {
  const navigate = useNavigate();
  const { transactions, setTransactions } = useStore();
  const activeMerchantId = getSelectedMerchantId();

  useEffect(() => {
    apiGetTransactions().then(setTransactions).catch(() => {});
  }, [setTransactions]);

  const grouped = (transactions || []).reduce((acc, tx) => {
    const d = tx.created_at ? new Date(tx.created_at) : new Date();
    const month = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    if (!acc[month]) acc[month] = [];
    acc[month].push(tx);
    return acc;
  }, {});

  return (
    <div className="px-4 pt-6 pb-6 space-y-5" data-testid="history-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(activeMerchantId ? storeRoute(activeMerchantId, 'wallet') : '/store')} className="p-1.5 rounded-lg hover:bg-secondary" data-testid="history-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>History</h1>
          <p className="text-xs text-muted-foreground">Your complete activity log</p>
        </div>
      </div>

      {Object.entries(grouped).map(([month, txs]) => (
        <div key={month} className="space-y-2">
          <h3 className="text-xs tracking-widest uppercase font-bold text-muted-foreground px-1">{month}</h3>
          {txs.map((tx, i) => (
            <motion.div key={tx.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
              className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card" data-testid={`history-${tx.id}`}>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                tx.type === 'earn' ? 'bg-emerald-100 dark:bg-emerald-950' : 'bg-red-100 dark:bg-red-950'}`}>
                {tx.type === 'earn' ? <TrendingUp className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> : <TrendingDown className="w-4 h-4 text-red-600 dark:text-red-400" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{tx.title}</p>
                <p className="text-xs text-muted-foreground">{tx.detail}</p>
              </div>
              <div className="text-right">
                <span className={`text-sm font-bold ${tx.type === 'earn' ? 'text-emerald-600' : 'text-red-500'}`}>
                  {tx.type === 'earn' ? '+' : ''}{tx.points} pts
                </span>
                <p className="text-[10px] text-muted-foreground">{tx.created_at ? new Date(tx.created_at).toLocaleDateString() : ''}</p>
              </div>
            </motion.div>
          ))}
        </div>
      ))}
      {(!transactions || transactions.length === 0) && (
        <p className="text-center text-sm text-muted-foreground py-12">No transaction history yet</p>
      )}
    </div>
  );
}
